import React, { useState } from "react";
import { api, logChuck } from "@/lib/api";
import { toast } from "sonner";
import { Search, Loader2, Pill, ExternalLink, Anchor, Copy, Layers } from "lucide-react";

const SECTION_LABELS = {
  boxed_warning: "Boxed Warning",
  indications_and_usage: "Indications & Usage",
  dosage_and_administration: "Dosage & Administration",
  dosage_forms_and_strengths: "Dosage Forms & Strengths",
  contraindications: "Contraindications",
  warnings_and_cautions: "Warnings & Precautions",
  warnings: "Warnings",
  adverse_reactions: "Adverse Reactions",
  drug_interactions: "Drug Interactions",
  use_in_specific_populations: "Use in Specific Populations",
  clinical_pharmacology: "Clinical Pharmacology",
  clinical_studies: "Clinical Studies",
  how_supplied: "How Supplied / Storage",
  active_ingredient: "Active Ingredient",
  purpose: "Purpose",
};

const TTY_LABELS = {
  IN: "Ingredient",
  PIN: "Precise Ingredient",
  MIN: "Multiple Ingredients",
  BN: "Brand Name",
  SCD: "Semantic Clinical Drug",
  SBD: "Semantic Branded Drug",
  GPCK: "Generic Pack",
  BPCK: "Brand Pack",
};

export default function LabelLookupPanel({ onAnchor }) {
  const [q, setQ] = useState("");
  const [dm, setDm] = useState({ results: [], total: 0 });     // DailyMed
  const [rx, setRx] = useState(null);                          // RxNorm
  const [loading, setLoading] = useState(false);
  const [openSet, setOpenSet] = useState(null);                // active setid label object
  const [labelLoading, setLabelLoading] = useState(false);

  const runSearch = async () => {
    const query = q.trim();
    if (!query) return;
    setLoading(true);
    setDm({ results: [], total: 0 });
    setRx(null);
    setOpenSet(null);
    try {
      const [r1, r2] = await Promise.all([
        api.get("/therapeutics/dailymed/search", { params: { q: query, pagesize: 15 } }),
        api.get("/therapeutics/rxnorm/search", { params: { q: query } }),
      ]);
      setDm(r1.data || { results: [], total: 0 });
      setRx(r2.data || null);
      if ((r1.data?.results || []).length === 0 && !(r2.data?.by_term_type && Object.keys(r2.data.by_term_type).length)) {
        toast.info("No results — try INN, brand, or a common trade name");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const openLabel = async (setid) => {
    setLabelLoading(true);
    try {
      const { data } = await api.get(`/therapeutics/dailymed/label/${setid}`);
      setOpenSet(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Label load failed");
    } finally {
      setLabelLoading(false);
    }
  };

  const anchorToDrafter = (row) => {
    if (!onAnchor) {
      toast.info("Open Therapeutics Drafting to anchor a draft in this label");
      return;
    }
    // Deduce a sensible active_ingredient/product from the label
    const brand = openSet?.openfda?.brand_name?.[0] || (row.title || "").split("(")[0].trim();
    const generic = openSet?.openfda?.generic_name?.[0] ||
      (row.title.match(/\((.*?)\)/) || [])[1] || row.title;
    const anchor = {
      setid: row.setid,
      title: row.title,
      brand,
      generic,
      marketer: row.marketer,
    };
    onAnchor(anchor);
    logChuck("label_anchored", { doc_type: "label_lookup", payload: {
      setid: row.setid, brand, generic,
    } });
    toast.success(`Anchored — opening Therapeutics Drafting with ${brand || generic}`);
  };

  return (
    <div data-testid="label-lookup-root" className="max-w-7xl">
      <div className="mb-6">
        <div className="overline mb-2 text-[#2563EB]">DRUG LABEL LOOKUP · DailyMed + RxNorm (NLM)</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Official FDA labeling + standardized drug names.
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Live NLM DailyMed SPL search paired with RxNorm concept resolution. Pick any label and anchor it into a
          CSR / PSUR / Protocol / REMS draft — every claim will cite the DailyMed setid.
        </p>
      </div>

      <div className="mb-4 flex gap-3">
        <div className="flex flex-1 items-center border border-slate-200 bg-white pl-4">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="label-lookup-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            placeholder="brand or INN (e.g. Advil, ibuprofen, atorvastatin, Humira)"
            className="flex-1 border-none bg-transparent px-3 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
        </div>
        <button
          data-testid="label-lookup-run"
          onClick={runSearch}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Search
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Left: DailyMed results */}
        <div className="lg:col-span-5">
          <div className="border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs">
              <span className="overline text-slate-500">
                DAILYMED SPL {dm.total > 0 ? `· ${dm.total.toLocaleString()} matches · showing ${dm.results.length}` : ""}
              </span>
            </div>
            {dm.results.length === 0 && !loading ? (
              <div className="p-6 text-center text-sm text-slate-500">Search a drug name to see labels.</div>
            ) : (
              <div data-testid="dm-results" className="max-h-[70vh] overflow-y-auto">
                {dm.results.map((row) => (
                  <button
                    key={row.setid}
                    data-testid={`dm-result-${row.setid.slice(0, 8)}`}
                    onClick={() => openLabel(row.setid)}
                    className={`block w-full border-b border-slate-100 p-3 text-left text-sm transition-colors last:border-b-0 hover:bg-slate-50 ${openSet?.setid === row.setid ? "bg-[#2563EB]/5" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      <Pill className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#2563EB]" strokeWidth={1.5} />
                      <div className="flex-1">
                        <div className="text-xs font-medium text-slate-900">{row.title}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-500">
                          <span className="font-mono">v{row.spl_version}</span>
                          <span>·</span>
                          <span>{row.published_date}</span>
                          {row.marketer && <><span>·</span><span>{row.marketer}</span></>}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RxNorm concepts */}
          {rx && Object.keys(rx.by_term_type || {}).length > 0 && (
            <div className="mt-4 border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs">
                <span className="overline text-slate-500">
                  RXNORM · {rx.approximate_match ? `RxCUI ${rx.approximate_match.rxcui} · score ${rx.approximate_match.score.toFixed(1)}` : ""}
                </span>
              </div>
              <div data-testid="rx-concepts" className="max-h-72 overflow-y-auto p-3 text-xs">
                {Object.entries(rx.by_term_type).map(([tty, concepts]) => (
                  <div key={tty} className="mb-3 last:mb-0">
                    <div className="overline mb-1 text-[10px] text-[#2563EB]">
                      {tty} · {TTY_LABELS[tty] || tty} · {concepts.length}
                    </div>
                    <div className="space-y-1">
                      {concepts.slice(0, 5).map((c) => (
                        <div key={c.rxcui} className="border border-slate-100 bg-white px-2 py-1 text-[11px] text-slate-700">
                          <span className="font-mono text-[9px] text-slate-400">{c.rxcui}</span> · {c.name}
                        </div>
                      ))}
                      {concepts.length > 5 && (
                        <div className="text-[10px] text-slate-400">+ {concepts.length - 5} more</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: Label detail */}
        <div className="lg:col-span-7">
          <div className="min-h-[420px] border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-[#2563EB]" />
                <span className="overline text-[#2563EB]">SPL LABEL</span>
                {labelLoading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
              {openSet && (
                <div className="flex items-center gap-2">
                  <a
                    href={openSet.dailymed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400"
                    data-testid="label-open-dailymed"
                  >
                    <ExternalLink className="h-3 w-3" /> DailyMed
                  </a>
                  <button
                    data-testid="label-anchor-drafter"
                    onClick={() => anchorToDrafter({ setid: openSet.setid, title: dm.results.find((r) => r.setid === openSet.setid)?.title || "", marketer: dm.results.find((r) => r.setid === openSet.setid)?.marketer })}
                    className="inline-flex items-center gap-1.5 border border-[#2563EB] bg-[#2563EB] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#1D4ED8]"
                  >
                    <Anchor className="h-3 w-3" /> Anchor to Drafter
                  </button>
                  <button
                    data-testid="label-copy-setid"
                    onClick={() => { navigator.clipboard.writeText(openSet.setid); toast.success("setid copied"); }}
                    className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400"
                  >
                    <Copy className="h-3 w-3" /> setid
                  </button>
                </div>
              )}
            </div>
            <div data-testid="label-detail" className="max-h-[75vh] overflow-y-auto p-6">
              {!openSet ? (
                <div className="flex h-96 flex-col items-center justify-center text-center text-slate-500">
                  <Pill className="mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="text-sm">Click a DailyMed result on the left to see the label.</div>
                  <div className="mt-1 text-xs text-slate-400">Then hit <b>Anchor to Drafter</b> to ground a CSR / PSUR / Protocol / REMS in this label.</div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border border-slate-200 bg-slate-50 p-3 text-xs">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="border border-[#2563EB]/30 bg-white px-1.5 py-0.5 font-mono text-[10px] text-[#1D4ED8]">
                        setid {openSet.setid.slice(0, 8)}…
                      </span>
                      <span className="text-slate-600">effective {openSet.effective_time}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-[11px] text-slate-600">
                      <div><b>Brand:</b> {openSet.openfda?.brand_name?.join(", ") || "—"}</div>
                      <div><b>Generic:</b> {openSet.openfda?.generic_name?.join(", ") || "—"}</div>
                      <div><b>Route:</b> {openSet.openfda?.route?.join(", ") || "—"}</div>
                      <div><b>Application #:</b> {openSet.openfda?.application_number?.join(", ") || "—"}</div>
                    </div>
                  </div>
                  {Object.entries(openSet.sections || {}).map(([key, val]) => (
                    <div key={key} className="border-l-2 border-slate-200 pl-3">
                      <div className="overline mb-1 text-[10px] text-[#2563EB]">{SECTION_LABELS[key] || key}</div>
                      <div className="whitespace-pre-wrap text-xs leading-relaxed text-slate-700">
                        {val.length > 1200 ? val.slice(0, 1200) + "…" : val}
                      </div>
                    </div>
                  ))}
                  {Object.keys(openSet.sections || {}).length === 0 && (
                    <div className="text-center text-sm text-slate-500">No structured sections available for this SPL.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
